/** @file validateDoc rules for IK chains (§6.5): chain shape, child inherit (setup and keys), target. */
import type { Doc } from './schema';
import type { IkConstraintDoc } from './schemaParts/skeleton';
import { makeError, type Pose2DError } from '../pose2dError';

/** The §6.5 reason for a second bone that does not inherit normally (setup or key). */
const CHILD_INHERIT = 'the child bone must use inherit "normal"';

/** The first §6.5 rule `c` breaks, or null (missing bones are NOT_FOUND elsewhere). */
function ikProblem(doc: Doc, c: IkConstraintDoc): string | null {
  const [parent, child] = c.bones;
  if (c.bones.includes(c.target)) return 'the target is one of the constrained bones';
  if (child === undefined) return null;
  const childBone = doc.bones[child];
  if (!childBone) return null;
  if (childBone.parent !== parent) return 'the second bone must be a direct child of the first';
  if (childBone.inherit !== 'normal') return CHILD_INHERIT;
  return null;
}

/**
 * IK_INVALID at the first non-normal inherit key of each animation on the second bone of a two-bone chain: the solve
 * aims that bone as if it inherited normally, so such a key makes the chain miss its target (§6.5).
 */
function checkChildInheritKeys(doc: Doc, c: IkConstraintDoc, out: Pose2DError[]): void {
  const child = c.bones[1];
  if (child === undefined) return;
  for (const [animId, anim] of Object.entries(doc.animations)) {
    const keys = anim.bones[child]?.inherit ?? [];
    const k = keys.findIndex((key) => key.v !== 'normal');
    const key = keys[k];
    if (key === undefined) continue;
    const hint = `animation "${anim.name}" keys inherit "${key.v}" at frame ${key.f} on the second bone of IK ${c.name}; the two-bone solve needs "normal"`;
    out.push(makeError('IK_INVALID', { ik: c.name, reason: CHILD_INHERIT }, { path: `animations.${animId}.bones.${child}.inherit.${k}`, hint }));
  }
}

/**
 * Appends IK_INVALID for constraints that break the §6.5 chain rules (setup and animated inherit of the child included).
 * compileIk runs it too; IK dependency loops (§6.6) are checkDocIkCycles', so the compiler keeps its own CYCLE form.
 */
export function checkDocIk(doc: Doc, out: Pose2DError[]): void {
  for (const [id, c] of Object.entries(doc.constraints)) {
    const reason = ikProblem(doc, c);
    if (reason !== null) out.push(makeError('IK_INVALID', { ik: c.name, reason }, { path: `constraints.${id}` }));
    checkChildInheritKeys(doc, c, out);
  }
}
